import type { Dispatch, SetStateAction, ReactNode } from "react";
import { useRef, useState } from "react";
import { ArrowUp, Bell, Download, Eye, Minus, Plus, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { TextVariableInput as Input } from "@/components/ui/text-variable-input";
import { TextVariableTextarea as Textarea } from "@/components/ui/text-variable-textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ColorPicker } from "@/components/editor/ColorPicker";
import { CollectionItemsPanel, type CollectionItemsConfig } from "@/components/skin/CollectionItemsPanel";
import { useToast } from "@/hooks/use-toast";
import { useEditorStore } from "@/store/editorStore";

type SimplePanorama = { id: string; name: string };

type ActionsPanelProps = {
  actions: Array<Record<string, unknown>>;
  panoramas?: SimplePanorama[];
  hotspots?: Array<{ id: string; name?: string }>;
  skinComponents?: Array<{ id: string; name: string; parentId?: string }>;
  excludeHotspotId?: string;
  isActionMenuOpen: boolean;
  setIsActionMenuOpen: (open: boolean) => void;
  expandedActionIds: Set<string>;
  setExpandedActionIds: Dispatch<SetStateAction<Set<string>>>;
  hotspotVisibilityConfigs?: Record<string, CollectionItemsConfig>;
  setHotspotVisibilityConfigs?: Dispatch<SetStateAction<Record<string, CollectionItemsConfig>>>;
  onAddAction: (type: string) => void;
  onRemoveAction: (actionId: string) => void;
  onUpdateAction: (actionId: string, updates: Record<string, unknown>) => void;
  menuExtras?: ReactNode;
  renderDownloadFields?: (action: Record<string, unknown>, actionId: string) => ReactNode;
};

export const ActionsPanel = ({
  actions,
  panoramas = [],
  hotspots = [],
  skinComponents = [],
  excludeHotspotId,
  isActionMenuOpen,
  setIsActionMenuOpen,
  expandedActionIds,
  setExpandedActionIds,
  hotspotVisibilityConfigs,
  setHotspotVisibilityConfigs,
  onAddAction,
  onRemoveAction,
  onUpdateAction,
  menuExtras,
  renderDownloadFields,
}: ActionsPanelProps) => {
  const { toast } = useToast();
  const currentCameraView = useEditorStore((state) => state.currentCameraView);
  const selectedPanorama = useEditorStore((state) => state.selectedPanorama);
  const setSelectedPanorama = useEditorStore((state) => state.setSelectedPanorama);
  const pendingManualReturnRef = useRef<Record<string, string>>({});
  const [durationDraftByActionId, setDurationDraftByActionId] = useState<Record<string, string>>({});

  const renderActionCard = (action: Record<string, unknown>) => {
    const actionId = action.id as string;
    const actionType = action.type as string;
    const trigger = (action.trigger as string) || "click";
    const isExpanded = expandedActionIds.has(actionId);

    return (
      <div key={actionId} className="rounded-md bg-gray-800/60 mt-2">
        <div
          role="button"
          tabIndex={0}
          className="w-full py-2 px-2 flex items-center justify-between text-left bg-gray-700/50 rounded"
          onClick={() =>
            setExpandedActionIds((prev) => {
              const next = new Set(prev);
              if (next.has(actionId)) next.delete(actionId);
              else next.add(actionId);
              return next;
            })
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setExpandedActionIds((prev) => {
                const next = new Set(prev);
                if (next.has(actionId)) next.delete(actionId);
                else next.add(actionId);
                return next;
              });
            }
          }}
        >
          <div className="min-w-0 flex items-center gap-2">
            {actionType === "notification" ? (
              <Bell size={16} className="text-gray-400" />
            ) : actionType === "download" ? (
              <Download size={16} className="text-gray-400" />
            ) : actionType === "showHideSkin" || actionType === "showHideHotspots" ? (
              <Eye size={16} className="text-gray-400" />
            ) : (
              <ArrowUp size={16} className="text-gray-400" />
            )}
            <div className="text-xs text-white font-medium">
              {actionType === "notification"
                ? "Notification"
                : actionType === "download"
                  ? "Download File"
                  : actionType === "showHideSkin"
                    ? "Show/Hide Skin"
                    : actionType === "showHideHotspots"
                      ? "Show/Hide Hotspots"
                      : "Navigate to"}
            </div>
          </div>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-200 hover:bg-gray-600 p-1 rounded transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveAction(actionId);
            }}
            title="Remove"
          >
            <Minus size={12} />
          </button>
        </div>

        {isExpanded && (
          <div className="pt-1 pb-3 space-y-1">
            <div className="flex items-center gap-2">
              <Label className="text-[11px] text-gray-400 w-1/3">Trigger</Label>
              <div className="w-2/3">
                <Select
                  value={trigger}
                  onValueChange={(value) => onUpdateAction(actionId, { trigger: value })}
                >
                  <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent side="bottom" align="start">
                    <SelectItem value="click">On Click</SelectItem>
                    <SelectItem value="mouseenter">Mouse enter</SelectItem>
                    <SelectItem value="mouseleave">Mouse leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-[11px] text-gray-400 w-1/3">Action</Label>
              <div className="w-2/3">
                <Select
                  value={actionType}
                  onValueChange={(value) => {
                    if (value === "showHideHotspots" && !action.hotspotVisibilityAction) {
                      onUpdateAction(actionId, { type: value, hotspotVisibilityAction: "show" });
                      return;
                    }
                    if (value === "showHideSkin" && !action.skinVisibilityAction) {
                      onUpdateAction(actionId, { type: value, skinVisibilityAction: "show" });
                      return;
                    }
                    onUpdateAction(actionId, { type: value });
                  }}
                >
                  <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                    <span>
                      {actionType === "navigate"
                        ? "Navigate to"
                        : actionType === "notification"
                          ? "Notification"
                          : actionType === "download"
                            ? "Download File"
                            : actionType === "showHideSkin"
                              ? "Show/Hide Skin"
                              : actionType === "showHideHotspots"
                                ? "Show/Hide Hotspots"
                                : actionType}
                    </span>
                  </SelectTrigger>
                  <SelectContent side="bottom" align="start">
                    <SelectItem value="navigate">
                      <div className="flex items-center gap-2">
                        <ArrowUp size={14} />
                        <span>Navigate to</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="notification">
                      <div className="flex items-center gap-2">
                        <Bell size={14} />
                        <span>Notification</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="download">
                      <div className="flex items-center gap-2">
                        <Download size={14} />
                        <span>Download File</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="showHideSkin">
                      <div className="flex items-center gap-2">
                        <Eye size={14} />
                        <span>Show/Hide Skin</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="showHideHotspots">
                      <div className="flex items-center gap-2">
                        <Eye size={14} />
                        <span>Show/Hide Hotspots</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {actionType === "navigate" && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-[11px] text-gray-400 w-1/3">Destination</Label>
                  <div className="w-2/3">
                    <Select
                      value={(action.linkedPanoramaId as string) || ""}
                      onValueChange={(value) => {
                        if (value) onUpdateAction(actionId, { linkedPanoramaId: value });
                      }}
                    >
                      <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                        <SelectValue placeholder="Select panorama" />
                      </SelectTrigger>
                      <SelectContent side="bottom" align="start">
                        {panoramas.map((panorama) => (
                          <SelectItem key={panorama.id} value={panorama.id}>
                            {panorama.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-[11px] text-gray-400 w-1/3">Transition</Label>
                  <div className="w-2/3">
                    {(() => {
                      const currentTransition = (action.linkTransitionType as string) || "default";
                      const transitionChoice =
                        currentTransition === "slide-to"
                          ? "slide-to"
                          : currentTransition === "fade" || currentTransition === "fade-same-view"
                            ? "fade"
                            : "none";
                      const noneViewChoice =
                        currentTransition === "same-view" ? "same-view" : "default";
                      const fadeViewChoice =
                        currentTransition === "fade-same-view" ? "fade-same-view" : "fade";

                      return (
                        <Select
                          value={transitionChoice}
                          onValueChange={(value) => {
                            if (value === "slide-to") {
                              onUpdateAction(actionId, { linkTransitionType: "slide-to" });
                              return;
                            }
                            if (value === "fade") {
                              onUpdateAction(actionId, { linkTransitionType: fadeViewChoice });
                              return;
                            }
                            onUpdateAction(actionId, { linkTransitionType: noneViewChoice });
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent side="bottom" align="start">
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="fade">Fade</SelectItem>
                            <SelectItem value="slide-to">Slide To</SelectItem>
                          </SelectContent>
                        </Select>
                      );
                    })()}
                  </div>
                </div>
                {(action.linkTransitionType as string) === "slide-to" && (
                  <div className="flex items-center gap-2">
                    <Label className="text-[11px] text-gray-400 w-1/3">View</Label>
                    <div className="w-2/3 space-y-1">
                      {(() => {
                        const explicitViewMode = action.linkTransitionViewMode as string | undefined;
                        const viewMode =
                          explicitViewMode === "manual" ||
                          (!explicitViewMode && action.linkTransitionDirectionView)
                            ? "manual"
                            : "automatic";
                        const hasManualView = Boolean(action.linkTransitionDirectionView);
                        const targetPanoramaName =
                          panoramas.find((panorama) => panorama.id === action.linkedPanoramaId)
                            ?.name ?? "target panorama";

                        return (
                          <>
                            <Select
                              value={viewMode}
                              onValueChange={(value) =>
                                onUpdateAction(actionId, {
                                  linkTransitionViewMode: value === "manual" ? "manual" : "automatic",
                                })
                              }
                            >
                              <SelectTrigger className="h-7 w-full text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white justify-between">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent side="bottom" align="start">
                                <SelectItem value="automatic">Automatic</SelectItem>
                                <SelectItem value="manual">Manual</SelectItem>
                              </SelectContent>
                            </Select>
                            {viewMode === "manual" && (
                              <Button
                                type="button"
                                variant="outline"
                                className="w-full border-gray-600 text-gray-200 hover:bg-gray-700 h-7 text-[11px] justify-start"
                                onClick={() => {
                                  if (!action.linkedPanoramaId) {
                                    toast({
                                      variant: "destructive",
                                      title: "Select a destination",
                                      description:
                                        "Choose a destination panorama before setting the manual view.",
                                    });
                                    return;
                                  }
                                  if (!selectedPanorama || selectedPanorama.id !== action.linkedPanoramaId) {
                                    const targetPanorama = panoramas.find(
                                      (panorama) => panorama.id === action.linkedPanoramaId,
                                    );
                                    if (!targetPanorama) {
                                      toast({
                                        variant: "destructive",
                                        title: "Target panorama not found",
                                        description: "Select a valid destination panorama first.",
                                      });
                                      return;
                                    }
                                    if (selectedPanorama?.id) {
                                      pendingManualReturnRef.current[actionId] = selectedPanorama.id;
                                    }
                                    setSelectedPanorama(targetPanorama);
                                    toast({
                                      title: "Switched to target panorama",
                                      description: `Adjust the view in '${targetPanoramaName}', then click Set manual view.`,
                                    });
                                    return;
                                  }
                                  if (!currentCameraView) {
                                    toast({
                                      variant: "destructive",
                                      title: "No view selected",
                                      description: "Move the camera to the desired view before saving.",
                                    });
                                    return;
                                  }
                                  onUpdateAction(actionId, {
                                    linkTransitionViewMode: "manual",
                                    linkTransitionDirectionView: {
                                      yaw: currentCameraView.yaw,
                                      pitch: currentCameraView.pitch,
                                      fov: currentCameraView.fov,
                                    },
                                  });
                                  const returnPanoramaId = pendingManualReturnRef.current[actionId];
                                  if (returnPanoramaId) {
                                    delete pendingManualReturnRef.current[actionId];
                                    const returnPanorama = panoramas.find(
                                      (panorama) => panorama.id === returnPanoramaId,
                                    );
                                    if (returnPanorama) {
                                      setSelectedPanorama(returnPanorama);
                                    }
                                  }
                                }}
                              >
                                {hasManualView ? "Update manual view" : "Set manual view"}
                              </Button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
                {(action.linkTransitionType as string) !== "slide-to" && (
                  <div className="flex items-center gap-2">
                    <Label className="text-[11px] text-gray-400 w-1/3">View</Label>
                    <div className="w-2/3">
                      {(() => {
                        const targetPanoramaName =
                          panoramas.find((panorama) => panorama.id === action.linkedPanoramaId)
                            ?.name ?? "target panorama";
                        const isFadeTransition =
                          (action.linkTransitionType as string) === "fade" ||
                          (action.linkTransitionType as string) === "fade-same-view";
                        const viewValue = isFadeTransition
                          ? (action.linkTransitionType as string) === "fade-same-view"
                            ? "fade-same-view"
                            : "fade"
                          : (action.linkTransitionType as string) === "same-view"
                            ? "same-view"
                            : "default";

                        return (
                          <Select
                            value={viewValue}
                            onValueChange={(value) => onUpdateAction(actionId, { linkTransitionType: value })}
                          >
                            <SelectTrigger className="h-7 w-full text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white justify-start">
                              <SelectValue className="truncate" />
                            </SelectTrigger>
                            <SelectContent side="bottom" align="start">
                              {isFadeTransition ? (
                                <>
                                  <SelectItem value="fade">{`Default view of '${targetPanoramaName}'`}</SelectItem>
                                  <SelectItem value="fade-same-view">Current camera position</SelectItem>
                                </>
                              ) : (
                                <>
                                  <SelectItem value="default">{`Default view of '${targetPanoramaName}'`}</SelectItem>
                                  <SelectItem value="same-view">Current camera position</SelectItem>
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        );
                      })()}
                    </div>
                  </div>
                )}
                {(() => {
                  const isFadeTransition =
                    (action.linkTransitionType as string) === "fade" ||
                    (action.linkTransitionType as string) === "fade-same-view";
                  const isSlideToTransition = (action.linkTransitionType as string) === "slide-to";
                  if (!isFadeTransition && !isSlideToTransition) return null;

                  const fadeColor = (action.linkTransitionFadeColor as string) || "transparent";
                  const defaultDurationSec = isSlideToTransition ? 1 : 0.5;
                  const fadeDurationSec =
                    typeof action.linkTransitionFadeDurationSec === "number" &&
                    Number.isFinite(action.linkTransitionFadeDurationSec)
                      ? action.linkTransitionFadeDurationSec
                      : defaultDurationSec;
                  const durationInputValue =
                    durationDraftByActionId[actionId] ?? String(fadeDurationSec);

                  return (
                    <>
                      {isFadeTransition && (
                        <div className="flex items-center gap-2">
                          <Label className="text-[11px] text-gray-400 w-1/3">Fade Color</Label>
                          <div className="w-2/3">
                            <ColorPicker
                              value={fadeColor}
                              onChange={(color) =>
                                onUpdateAction(actionId, { linkTransitionFadeColor: color })
                              }
                              label="Fade Color"
                              labelClassName="sr-only"
                            />
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Label className="text-[11px] text-gray-400 w-1/3">Duration</Label>
                        <div className="w-2/3">
                          <div className="flex items-center gap-2">
                            <Input
                              className="h-7 text-xs bg-gray-700/60 border-0 text-white"
                              type="text"
                              inputMode="decimal"
                              pattern="^[0-9]*([.,][0-9]+)?$"
                              value={durationInputValue}
                              onChange={(e) => {
                                const rawValue = e.target.value.trim();
                                setDurationDraftByActionId((prev) => ({
                                  ...prev,
                                  [actionId]: rawValue,
                                }));
                                if (rawValue === "") {
                                  onUpdateAction(actionId, {
                                    linkTransitionFadeDurationSec: defaultDurationSec,
                                  });
                                  return;
                                }
                                if (!/^\d*([.,]\d*)?$/.test(rawValue)) return;
                                if (rawValue.endsWith(",") || rawValue.endsWith(".")) return;
                                const normalizedValue = rawValue.replace(",", ".");
                                const nextValue = Number.parseFloat(normalizedValue);
                                onUpdateAction(actionId, {
                                  linkTransitionFadeDurationSec: Number.isFinite(nextValue)
                                    ? Math.max(0, nextValue)
                                    : defaultDurationSec,
                                });
                              }}
                              onBlur={() => {
                                const draftValue = durationDraftByActionId[actionId];
                                if (draftValue == null) return;
                                const rawValue = draftValue.trim();
                                if (rawValue === "") {
                                  onUpdateAction(actionId, {
                                    linkTransitionFadeDurationSec: defaultDurationSec,
                                  });
                                  return;
                                }
                                const normalizedValue = rawValue.replace(",", ".");
                                const nextValue = Number.parseFloat(normalizedValue);
                                onUpdateAction(actionId, {
                                  linkTransitionFadeDurationSec: Number.isFinite(nextValue)
                                    ? Math.max(0, nextValue)
                                    : defaultDurationSec,
                                });
                              }}
                            />
                            <span className="text-[11px] text-gray-400">seconds</span>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </>
            )}

            {actionType === "notification" && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-[11px] text-gray-400 w-1/3">Title</Label>
                  <Input
                    className="w-2/3 h-7 text-xs bg-gray-700/60 border-0 text-white"
                    value={(action.notificationTitle as string) || ""}
                    onChange={(e) => onUpdateAction(actionId, { notificationTitle: e.target.value })}
                  />
                </div>
                <div className="flex items-start gap-2">
                  <Label className="text-[11px] text-gray-400 w-1/3 pt-1">Message</Label>
                  <Textarea
                    className="w-2/3 text-xs bg-gray-700/60 border-0 text-white min-h-[60px]"
                    value={(action.notificationMessage as string) || ""}
                    onChange={(e) =>
                      onUpdateAction(actionId, { notificationMessage: e.target.value })
                    }
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-[11px] text-gray-400 w-1/3">Style</Label>
                  <div className="w-2/3">
                    <Select
                      value={(action.notificationStyle as string) || "toast"}
                      onValueChange={(value) => onUpdateAction(actionId, { notificationStyle: value })}
                    >
                      <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent side="bottom" align="start">
                        <SelectItem value="banner">Banner</SelectItem>
                        <SelectItem value="alert">Alert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {(action.notificationStyle as string) !== "alert" && (
                  <div className="flex items-center gap-2">
                    <Label className="text-[11px] text-gray-400 w-1/3">Auto-hide (ms)</Label>
                    <Input
                      type="number"
                      className="w-2/3 h-7 text-xs bg-gray-700/60 border-0 text-white"
                      value={(action.notificationAutoHideDuration as number | undefined) ?? ""}
                      onChange={(e) =>
                        onUpdateAction(actionId, {
                          notificationAutoHideDuration: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                )}
              </>
            )}

            {actionType === "showHideSkin" && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-[11px] text-gray-400 w-1/3">Action</Label>
                  <div className="w-2/3">
                    <Select
                      value={(action.skinVisibilityAction as string) || "show"}
                      onValueChange={(value) => onUpdateAction(actionId, { skinVisibilityAction: value })}
                    >
                      <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent side="bottom" align="start">
                        <SelectItem value="show">Show</SelectItem>
                        <SelectItem value="hide">Hide</SelectItem>
                        <SelectItem value="toggle">Toggle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Label className="text-[11px] text-gray-400 w-1/3 pt-1">Components</Label>
                  <div className="w-2/3 max-h-48 overflow-y-auto space-y-0">
                    {skinComponents.length === 0 ? (
                      <p className="text-[10px] text-gray-500 p-2">No skin components</p>
                    ) : (
                      skinComponents.filter((comp) => !comp.parentId).map((comp) => {
                        const selected = ((action.skinVisibilityTargetComponentIds as string[]) || []).includes(
                          comp.id,
                        );
                        const children = skinComponents.filter((c) => c.parentId === comp.id);
                        const isExpandedChild = expandedActionIds.has(`skin-${actionId}-${comp.id}`);

                        return (
                          <div key={comp.id} className="space-y-0">
                            <div className="flex items-center gap-1 px-2 py-1 hover:bg-gray-700/50 rounded text-[11px] text-gray-200">
                              {children.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setExpandedActionIds((prev) => {
                                      const next = new Set(prev);
                                      const key = `skin-${actionId}-${comp.id}`;
                                      if (next.has(key)) next.delete(key);
                                      else next.add(key);
                                      return next;
                                    });
                                  }}
                                  className="p-0 text-gray-400 hover:text-gray-200 transition-colors"
                                >
                                  <ChevronDown
                                    size={14}
                                    className={`transition-transform ${isExpandedChild ? "" : "-rotate-90"}`}
                                  />
                                </button>
                              )}
                              {children.length === 0 && <div className="w-[14px]" />}
                              <label className="flex items-center gap-2 cursor-pointer flex-1">
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={(e) => {
                                    const current =
                                      (action.skinVisibilityTargetComponentIds as string[]) || [];
                                    const next = e.target.checked
                                      ? [...current, comp.id]
                                      : current.filter((id) => id !== comp.id);
                                    onUpdateAction(actionId, {
                                      skinVisibilityTargetComponentIds: next,
                                    });
                                  }}
                                  className="h-3 w-3 accent-gray-500 cursor-pointer"
                                />
                                <span>{comp.name}</span>
                              </label>
                            </div>
                            {isExpandedChild &&
                              children.map((child) => {
                                const childSelected = ((action.skinVisibilityTargetComponentIds as string[]) || []).includes(
                                  child.id,
                                );
                                return (
                                  <label
                                    key={child.id}
                                    className="flex items-center gap-2 px-8 py-1 hover:bg-gray-700/50 cursor-pointer rounded text-[11px] text-gray-300"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={childSelected}
                                      onChange={(e) => {
                                        const current =
                                          (action.skinVisibilityTargetComponentIds as string[]) || [];
                                        const next = e.target.checked
                                          ? [...current, child.id]
                                          : current.filter((id) => id !== child.id);
                                        onUpdateAction(actionId, {
                                          skinVisibilityTargetComponentIds: next,
                                        });
                                      }}
                                      className="h-3 w-3 accent-gray-500 cursor-pointer"
                                    />
                                    <span>{child.name}</span>
                                  </label>
                                );
                              })}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}

            {actionType === "showHideHotspots" && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-[11px] text-gray-400 w-1/3">Action</Label>
                  <div className="w-2/3">
                    <Select
                      value={(action.hotspotVisibilityAction as string) || "show"}
                      onValueChange={(value) => onUpdateAction(actionId, { hotspotVisibilityAction: value })}
                    >
                      <SelectTrigger className="h-7 text-xs bg-gray-700/60 border-0 px-3 pr-2 rounded-md text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent side="bottom" align="start">
                        <SelectItem value="show">Show</SelectItem>
                        <SelectItem value="hide">Hide</SelectItem>
                        <SelectItem value="toggle">Toggle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Label className="text-[11px] text-gray-400 w-1/3 pt-0">Items</Label>
                  <div className="w-2/3">
                    <CollectionItemsPanel
                      config={
                        hotspotVisibilityConfigs?.[actionId] ||
                        (action.hotspotVisibilityItemsConfig as CollectionItemsConfig) || {
                          mode: "selected",
                          filters: [],
                          sort: null,
                          selectedItemIds: [],
                        }
                      }
                      fields={[
                        { name: "id", displayName: "ID", type: "string" },
                        { name: "name", displayName: "Name", type: "string" },
                      ]}
                      allItems={hotspots.filter((hotspot) => hotspot.id !== excludeHotspotId)}
                      onChange={(newConfig) => {
                        setHotspotVisibilityConfigs?.((prev) => ({
                          ...prev,
                          [actionId]: newConfig,
                        }));
                        onUpdateAction(actionId, {
                          hotspotVisibilityTargetIds: newConfig.selectedItemIds,
                          hotspotVisibilityItemsConfig: newConfig,
                        });
                      }}
                      compact={true}
                    />
                  </div>
                </div>
              </>
            )}

            {actionType === "download" && (
              <>
                {renderDownloadFields ? (
                  renderDownloadFields(action, actionId)
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <Label className="text-[11px] text-gray-400 w-1/3">File URL</Label>
                      <Input
                        className="w-2/3 h-7 text-xs bg-gray-700/60 border-0 text-white"
                        value={(action.downloadFileUrl as string) || ""}
                        onChange={(e) =>
                          onUpdateAction(actionId, { downloadFileUrl: e.target.value })
                        }
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-[11px] text-gray-400 w-1/3">File name</Label>
                      <Input
                        className="w-2/3 h-7 text-xs bg-gray-700/60 border-0 text-white"
                        value={(action.downloadFileName as string) || ""}
                        onChange={(e) =>
                          onUpdateAction(actionId, { downloadFileName: e.target.value })
                        }
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">ACTIONS</div>
        <Popover open={isActionMenuOpen} onOpenChange={setIsActionMenuOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="p-1 rounded text-gray-300 hover:text-white hover:bg-gray-700"
              onClick={(e) => e.stopPropagation()}
              aria-label="Add action"
            >
              <Plus size={16} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            forceMount
            className="w-56 bg-gray-800 border-gray-600 p-2 space-y-1"
            align="end"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start border-gray-600 text-gray-200 hover:bg-gray-700 h-8 text-xs"
              onClick={() => onAddAction("navigate")}
            >
              <ArrowUp size={12} className="mr-2" />
              Navigate to
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full justify-start border-gray-600 text-gray-200 hover:bg-gray-700 h-8 text-xs"
              onClick={() => onAddAction("showHideSkin")}
            >
              <Eye size={12} className="mr-2" />
              Show/Hide Skin
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full justify-start border-gray-600 text-gray-200 hover:bg-gray-700 h-8 text-xs"
              onClick={() => onAddAction("showHideHotspots")}
            >
              <Eye size={12} className="mr-2" />
              Show/Hide Hotspots
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full justify-start border-gray-600 text-gray-200 hover:bg-gray-700 h-8 text-xs"
              onClick={() => onAddAction("notification")}
            >
              <Bell size={12} className="mr-2" />
              Notification
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full justify-start border-gray-600 text-gray-200 hover:bg-gray-700 h-8 text-xs"
              onClick={() => onAddAction("download")}
            >
              <Download size={12} className="mr-2" />
              Download File
            </Button>

            {menuExtras && <Separator className="bg-gray-700 my-1" />}
            {menuExtras}
          </PopoverContent>
        </Popover>
      </div>
      <Separator className="bg-gray-700" />
      {actions.length > 0 ? (
        actions.map((action) => renderActionCard(action))
      ) : (
        <div className="text-center text-gray-400 py-4">
          <p className="text-sm">No actions applied to this component</p>
        </div>
      )}
    </>
  );
};
